import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Header } from "../components/Header";
import { ExerciseCard } from "../components/ExerciseCard";
import { AudioCard } from "../components/AudioCard";
import { Wind, AlertTriangle, BookOpen, Headphones, ChevronRight, Shield, MessageCircle, Heart, Unlock, ClipboardList, Compass, Eye } from "lucide-react";
import { motion } from "framer-motion";

const API = `/api`;

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } }
};
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

export default function Dashboard() {
  const { user } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [audios, setAudios] = useState([]);
  const [tips, setTips] = useState([]);

  useEffect(() => {
    axios.get(`${API}/exercises`).then(r => setExercises(r.data)).catch(() => {});
    axios.get(`${API}/audio/library`).then(r => setAudios(r.data)).catch(() => {});
    axios.get(`${API}/tips`).then(r => setTips(r.data)).catch(() => {});
    axios.post(`${API}/auth/mark-notifications-seen`).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="client-dashboard">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>Bonjour, {user?.name}</h1>
          <p className="text-[#6A7771] mb-10">Votre espace personnel de bien-être</p>
        </motion.div>

        {/* Bento grid tools */}
        <motion.div variants={stagger} initial="hidden" animate="show" className="grid grid-cols-2 md:grid-cols-12 gap-4 sm:gap-5 mb-10">
          <motion.div variants={fadeUp} className="col-span-2 md:col-span-12">
            <Link to="/orientation" className="block bg-[#2A3A31] text-white rounded-3xl p-6 sm:p-8 hover:shadow-[0_16px_48px_rgba(42,58,49,0.2)] transition-all duration-300 group" data-testid="tool-orientation">
              <div className="flex items-center gap-4">
                <Compass className="w-8 h-8 opacity-80 group-hover:rotate-45 transition-transform duration-500" strokeWidth={1.5} />
                <div>
                  <h3 className="text-lg font-semibold mb-0.5" style={{ fontFamily: 'Outfit' }}>Quel outil pour moi maintenant ?</h3>
                  <p className="text-white/60 text-sm">Trouvez l'outil adapté à ce que vous vivez en ce moment</p>
                </div>
                <ChevronRight className="w-5 h-5 opacity-50 group-hover:translate-x-1 transition-transform duration-300 ml-auto hidden sm:block" strokeWidth={1.5} />
              </div>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/coherence" className="block bg-gradient-to-br from-[#6B8576] to-[#546A5E] text-white rounded-3xl p-6 sm:p-8 hover:shadow-[0_16px_48px_rgba(107,133,118,0.25)] transition-all duration-300 group h-full" data-testid="tool-coherence">
              <Wind className="w-8 h-8 mb-4 opacity-80 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Cohérence cardiaque</h3>
              <p className="text-white/70 text-sm">Respiration guidée 4s / 8s</p>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/sos-yeux" className="block bg-gradient-to-br from-[#2A3A31] to-[#1A2620] text-white rounded-3xl p-6 sm:p-8 hover:shadow-[0_16px_48px_rgba(42,58,49,0.25)] transition-all duration-300 group h-full" data-testid="tool-emdr">
              <Eye className="w-8 h-8 mb-4 opacity-80 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>SOS mouvements oculaires</h3>
              <p className="text-white/60 text-sm">Quand ça déborde, suivez le point</p>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/crisis" className="block bg-gradient-to-br from-[#D9A892] to-[#C2937E] text-white rounded-3xl p-6 sm:p-8 hover:shadow-[0_16px_48px_rgba(217,168,146,0.25)] transition-all duration-300 group h-full" data-testid="tool-crisis">
              <AlertTriangle className="w-8 h-8 mb-4 opacity-80 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Carnet de crise</h3>
              <p className="text-white/70 text-sm">Notez et comprenez vos crises</p>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/exercises" className="block bg-white border border-[#EBE7E0] rounded-3xl p-6 sm:p-8 hover:shadow-[0_10px_40px_rgba(44,53,49,0.08)] transition-all duration-300 group h-full" data-testid="tool-exercises">
              <BookOpen className="w-8 h-8 mb-4 text-[#6B8576] opacity-70 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>Exercices</h3>
              <p className="text-[#6A7771] text-sm">14 exercices pratiques</p>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/audio" className="block bg-white border border-[#EBE7E0] rounded-3xl p-6 sm:p-8 hover:shadow-[0_10px_40px_rgba(44,53,49,0.08)] transition-all duration-300 group h-full" data-testid="tool-audio">
              <Headphones className="w-8 h-8 mb-4 text-[#D9A892] opacity-70 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>Mes audios</h3>
              <p className="text-[#6A7771] text-sm">Séances débloquées par votre thérapeute</p>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/boucliers" className="block bg-white border border-[#EBE7E0] rounded-3xl p-6 sm:p-8 hover:shadow-[0_10px_40px_rgba(44,53,49,0.08)] transition-all duration-300 group h-full" data-testid="tool-boucliers">
              <Shield className="w-8 h-8 mb-4 text-[#6B8576] opacity-70 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>Boucliers Paradoxaux</h3>
              <p className="text-[#6A7771] text-sm">Renvoyez de l'amour face aux missiles</p>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/accusation-retournee" className="block bg-white border border-[#EBE7E0] rounded-3xl p-6 sm:p-8 hover:shadow-[0_10px_40px_rgba(44,53,49,0.08)] transition-all duration-300 group h-full" data-testid="tool-accusation">
              <MessageCircle className="w-8 h-8 mb-4 text-[#D9A892] opacity-70 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>Accusation retournée</h3>
              <p className="text-[#6A7771] text-sm">Désamorcez les conflits avec douceur</p>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/sos-parent" className="block bg-white border border-[#EBE7E0] rounded-3xl p-6 sm:p-8 hover:shadow-[0_10px_40px_rgba(44,53,49,0.08)] transition-all duration-300 group h-full" data-testid="tool-sos-parent">
              <Heart className="w-8 h-8 mb-4 text-[#B86666] opacity-70 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>SOS parent</h3>
              <p className="text-[#6A7771] text-sm">Reprendre le contrôle en pleine tension</p>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/pour-le-moment" className="block bg-white border border-[#EBE7E0] rounded-3xl p-6 sm:p-8 hover:shadow-[0_10px_40px_rgba(44,53,49,0.08)] transition-all duration-300 group h-full" data-testid="tool-pour-le-moment">
              <Unlock className="w-8 h-8 mb-4 text-[#6B8576] opacity-70 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>Pour le moment</h3>
              <p className="text-[#6A7771] text-sm">Défiger une pensée bloquante</p>
            </Link>
          </motion.div>
          <motion.div variants={fadeUp} className="col-span-1 md:col-span-6">
            <Link to="/tests" className="block bg-white border border-[#EBE7E0] rounded-3xl p-6 sm:p-8 hover:shadow-[0_10px_40px_rgba(44,53,49,0.08)] transition-all duration-300 group h-full" data-testid="tool-tests">
              <ClipboardList className="w-8 h-8 mb-4 text-[#D9A892] opacity-70 group-hover:scale-110 transition-transform duration-300" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-[#2C3531] mb-1" style={{ fontFamily: 'Outfit' }}>Tests</h3>
              <p className="text-[#6A7771] text-sm">Mieux se connaître en quelques questions</p>
            </Link>
          </motion.div>
        </motion.div>

        {/* Tips */}
        {tips.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="mb-10">
            <h2 className="text-xl font-semibold text-[#2C3531] mb-4" style={{ fontFamily: 'Outfit' }}>Conseils de votre thérapeute</h2>
            <div className="space-y-3">
              {tips.slice(0, 3).map(tip => (
                <div key={tip.id} className="bg-white rounded-3xl border border-[#EBE7E0] p-5" data-testid={`tip-${tip.id}`}>
                  <h3 className="font-medium text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>{tip.title}</h3>
                  <p className="text-sm text-[#6A7771] mt-1.5 whitespace-pre-wrap leading-relaxed">{tip.content}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Audio sessions */}
        {audios.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>Mes séances audio</h2>
              <Link to="/audio" className="text-sm text-[#6B8576] font-medium hover:underline flex items-center gap-1">Voir tout <ChevronRight className="w-4 h-4" strokeWidth={1.5} /></Link>
            </div>
            <div className="space-y-3">
              {audios.slice(0, 3).map(a => <AudioCard key={a.id} audio={a} />)}
            </div>
          </div>
        )}

        {/* Exercises preview */}
        {exercises.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-[#2C3531]" style={{ fontFamily: 'Outfit' }}>Exercices pratiques</h2>
              <Link to="/exercises" className="text-sm text-[#6B8576] font-medium hover:underline flex items-center gap-1">Voir tout <ChevronRight className="w-4 h-4" strokeWidth={1.5} /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {exercises.slice(0, 6).map(ex => <ExerciseCard key={ex.id} exercise={ex} />)}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
